
import { Component, Vue, Ref, Prop, Watch } from 'vue-property-decorator'
import DrawingCanvas from '@/components/atoms/DrawingCanvas.vue'
import { DrawingModel } from '@/models/DrawingModel'

/**
 * 手書きエリア
 */
@Component({
  components: {
    DrawingCanvas,
  },
})
export default class DrawingArea extends Vue {
  /** アクティブなペン情報 */
  @Prop()
  private activePen!: DrawingModel.PenAsset

  /** 描画済みのストローク情報 */
  @Prop()
  private strokes!: DrawingModel.Stroke[]

  /** canvasコンポーネントアクセス用ref */
  @Ref()
  private canvas!: DrawingCanvas

  /** 描画中のストローク情報 */
  private currentStroke: DrawingModel.Point[] = []

  /** 描画状態を保持するフラグ(描画中はtrue) */
  private isDrawing = false

  /**
   * 描画情報の監視
   */
  @Watch('currentStroke')
  private updateCurrentStroke(): void {
    this.drawAllStrokes()
  }

  /**
   * 初期処理
   */
  private mounted(): void {
    // キャンバスの再描画を行う
    this.drawAllStrokes()
  }

  /**
   * 描画処理を実行
   */
  public drawAllStrokes(): void {
    // キャンバスのクリア
    this.canvas.context.beginPath()
    this.canvas.context.clearRect(0, 0, this.canvas.element.width, this.canvas.element.height)

    // 描画済みのストロークを出力
    this.strokes.forEach((stroke) => {
      this.drawStroke(stroke)
    })

    // 描画中のストロークを出力
    this.drawStroke({
      pen: this.activePen,
      points: this.currentStroke,
    })
  }

  /**
   * canvas幅を取得する
   *
   * @returns 幅
   */
  public getCanvasWidth(): number {
    return this.canvas.element.width
  }

  /**
   * canvas高さを取得する
   *
   * @returns 高さ
   */
  public getCanvasHeight(): number {
    return this.canvas.element.height
  }

  /**
   * ストロークの描画を実行
   *
   * @param stroke ストローク情報
   */
  private drawStroke(stroke: DrawingModel.Stroke): void {
    // ペン情報の設定
    this.canvas.context.globalCompositeOperation = 'source-over'
    this.canvas.context.lineWidth = stroke.pen.lineWidth
    this.canvas.context.lineCap = stroke.pen.lineCap
    this.canvas.context.lineJoin = stroke.pen.lineJoin
    this.canvas.context.strokeStyle = stroke.pen.color

    // 描画開始
    this.canvas.context.beginPath()

    stroke.points.forEach((point, i) => {
      if (i === 0) {
        // 配列の先頭だけmoveToを使用する
        this.canvas.context.moveTo(point.x, point.y)
      } else {
        this.canvas.context.lineTo(point.x, point.y)
      }
    })

    // 線の出力
    this.canvas.context.stroke()
  }

  /**
   * ドラッグ(描画)開始
   *
   * @param e htmlイベント
   */
  private dragStart(e: TouchEvent | MouseEvent): void {
    // 二重処理ブロック
    if (this.isDrawing) {
      return
    }
    this.isDrawing = true

    // スクロール抑制
    e.preventDefault()

    // 座標を計算し描画開始位置を設定
    this.currentStroke = []
    this.currentStroke.push(this.getDrawPoint(e))
  }

  /**
   * ドラッグ(描画)中
   *
   * @param e htmlイベント
   */
  private dragging(e: TouchEvent | MouseEvent): void {
    // 描画開始済みチェック
    if (!this.isDrawing) {
      return
    }

    // スクロール抑制
    e.preventDefault()

    // 座標を計算し描画位置を取得
    const point = this.getDrawPoint(e)

    // 最後に設定した座標と今回の描画位置が完全に同じ場合は追加せず終了
    const lastPoint = this.currentStroke[this.currentStroke.length - 1]
    if (lastPoint.x === point.x && lastPoint.y === point.y) {
      return
    }

    // 描画位置を設定
    this.currentStroke.push(point)
  }

  /**
   * ドラッグ(描画)終了
   *
   * @param e htmlイベント
   */
  private dragEnd(e: TouchEvent | MouseEvent): void {
    // 描画開始済みチェック
    if (!this.isDrawing) {
      return
    }

    // スクロール抑制
    e.preventDefault()

    // 消しゴムか否かで処理を変更
    if (this.activePen.isEraser) {
      // 消しゴムの場合の処理
      const newStrokes = this.affectEraserStroke()
      this.strokes.splice(0)
      this.strokes.push(...newStrokes)
    } else {
      // ペンの場合は描画中の情報をstrokeに追加
      //   座標が1つしかない場合は線にならないため追加しない
      if (this.currentStroke.length > 1) {
        this.strokes.push({
          pen: this.activePen,
          points: this.currentStroke,
        })
      }
    }

    this.currentStroke = []
    this.isDrawing = false
  }

  /**
   * 消しゴムのストローク情報を反映する
   *
   * @returns 反映後のストローク情報
   */
  private affectEraserStroke(): DrawingModel.Stroke[] {
    const strokes: DrawingModel.Stroke[] = []

    this.strokes.forEach((stroke) => {
      // 線が交差しているかを判定する
      let isIntersect = false

      for (let i = 1; i < stroke.points.length; i++) {
        // ストロークを交点単位で分割し、外郭の矩形情報を取得する
        const strokeXMin = Math.min(stroke.points[i].x, stroke.points[i - 1].x)
        const strokeXMax = Math.max(stroke.points[i].x, stroke.points[i - 1].x)
        const strokeYMin = Math.min(stroke.points[i].y, stroke.points[i - 1].y)
        const strokeYMax = Math.max(stroke.points[i].y, stroke.points[i - 1].y)

        for (let j = 1; j < this.currentStroke.length; j++) {
          // 消しゴムでのストロークを交点単位で分割し、外郭の矩形情報を取得する
          const eraserXMin = Math.min(this.currentStroke[j].x, this.currentStroke[j - 1].x)
          const eraserXMax = Math.max(this.currentStroke[j].x, this.currentStroke[j - 1].x)
          const eraserYMin = Math.min(this.currentStroke[j].y, this.currentStroke[j - 1].y)
          const eraserYMax = Math.max(this.currentStroke[j].y, this.currentStroke[j - 1].y)

          // 座標が重なっている場合は線が交差している=消しゴムで消す対象
          if (
            strokeXMin <= eraserXMax &&
            strokeYMin <= eraserYMax &&
            eraserXMin <= strokeXMax &&
            eraserYMin <= strokeYMax
          ) {
            isIntersect = true
            break
          }
        }

        if (isIntersect) {
          break
        }
      }

      // 線が交差していない場合は配列に格納
      if (!isIntersect) strokes.push(stroke)
    })

    return strokes
  }

  /**
   * 描画座標の取得
   *   イベント種類ごとの座標差を補正する
   *
   * @param e htmlイベント
   * @returns 描画用の座標
   */
  private getDrawPoint(e: TouchEvent | MouseEvent): DrawingModel.Point {
    if (e instanceof MouseEvent) {
      // PC用(マウスイベント)の処理
      return {
        x: e.offsetX,
        y: e.offsetY,
      }
    } else {
      // スマホ用(タッチイベント)の処理
      const clientRect = this.canvas.element.getBoundingClientRect()
      return {
        x: e.touches[0].pageX - clientRect.left - window.scrollX,
        y: e.touches[0].pageY - clientRect.top - window.scrollY,
      }
    }
  }
}
